import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import scrollToElement from '../../../helpers/scroll-to';
import { OrderType } from '../../../models/order-type';
import { OrdersListingType } from '../../../models/orders-listing-type';
import { OrdersPaginatedType } from '../../../models/orders-paginated-type';
import orderSearch from '../../../services/content-api/endpoints/order/search';
import LoadingSpinner from '../../atoms/loading-spinner/loading-spinner';
import TableHeading from '../../atoms/table-heading/table-heading';
import useGetSessionId from '../../providers/user/hooks/use-get-session-id.hook';
import ItemPagination from '../item-pagination/item-pagination';
import NoDataBlock from '../no-data-block/no-data-block';
import OrdersListingFilters from '../orders-listing-filters/orders-listing-filters';
import OrdersListingTableRow from '../orders-listing-table-row/orders-listing-table-row';
import useDebounce from '../../../hooks/use-debounce/use-debounce';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';

const OrdersListingTable: FC<OrdersListingType> = (
  props: OrdersListingType
) => {
  const {
    confirmationNumberColumnLabel,
    noOrdersDescription,
    noOrdersHeading,
    orderPlacedColumnLabel,
    statusColumnLabel,
    totalColumnLabel,
    yourReferenceColumnLabel,
  } = props;
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const initialDate = dayjs().startOf('month').toDate();
  const [filterAddress, setFilterAddress] = useState<number>(0);
  const [filterDate, setFilterDate] = useState<Date>(initialDate);
  const [filterSearchTerm, setFilterSearchTerm] = useState<string>('');
  const debouncedFilterSearchTerm = useDebounce(filterSearchTerm);
  const [filterStatus, setFilterStatus] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<OrdersPaginatedType>();
  const [page, setPage] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(true);
  const [sortDirection, setSortDirection] = useState<boolean>(true);
  const itemsPerPage = 15;
  const ordersContainer = useRef(null);
  const getSessionId = useGetSessionId();

  const resetPage = () => {
    setPage(0);
    setShowPagination(false);

    setTimeout(() => {
      setShowPagination(true);
    }, 0);
  };

  useEffect(() => {
    setLoading(true);

    getSessionId().then((sessionId) =>
      orderSearch(
        filterAddress,
        debouncedFilterSearchTerm,
        dayjs(filterDate).add(1, 'M').subtract(1, 's').format('YYYY-MM-DD'),
        sortDirection,
        page + 1,
        itemsPerPage,
        sessionId,
        dayjs(filterDate).format('YYYY-MM-DD'),
        filterStatus,
        true
      ).then((response: OrdersPaginatedType) => {
        setOrders(response);

        if (!response.orders.length) {
          setPage(0);
          setSortDirection(true);
        }

        setLoading(false);
      })
    );
  }, [
    filterAddress,
    filterDate,
    debouncedFilterSearchTerm,
    filterStatus,
    page,
    sortDirection,
  ]);

  useEffect(() => {
    if (debouncedFilterSearchTerm?.length >= 3) {
      pushToDataLayer(GtmEventNameEnum.OrdersSearch);
    }
  }, [debouncedFilterSearchTerm]);

  if (!orders) {
    return <LoadingSpinner className="h-96" />;
  }

  return (
    <div ref={ordersContainer}>
      <OrdersListingFilters
        {...props}
        initialDate={initialDate}
        onAddressChange={(address: number) => {
          resetPage();
          setFilterAddress(address);
        }}
        onDateChange={(date: Date) => {
          resetPage();
          setFilterDate(date);
        }}
        onSearchTermChange={(searchTerm: string) => {
          if (searchTerm.length <= 3 || loading) {
            return;
          }

          resetPage();
          setFilterSearchTerm(searchTerm);
        }}
        onStatusChange={(statuses: number) => {
          resetPage();
          setFilterStatus(statuses);
        }}
        onSearchIconClick={() => {
          resetPage();
          setFilterSearchTerm('');
        }}
      />

      {loading && <LoadingSpinner className="h-96" />}

      <AnimatePresence>
        {!orders?.orders.length && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
            <NoDataBlock
              heading={noOrdersHeading}
              message={noOrdersDescription}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!orders?.orders.length && (
          <motion.table
            animate={{ opacity: 1 }}
            className="mb-5 table-fixed w-full"
            initial={{ opacity: 0 }}
          >
            <thead className="hidden lg:table-header-group">
              <tr>
                <TableHeading
                  className="w-28 xl:w-56"
                  label={orderPlacedColumnLabel}
                  onSort={(direction: number) => {
                    resetPage();
                    setSortDirection(!(direction < 0));
                  }}
                />
                <TableHeading label={confirmationNumberColumnLabel} />
                <TableHeading label={yourReferenceColumnLabel} />
                <TableHeading className="w-40" label={statusColumnLabel} />
                <TableHeading label={totalColumnLabel} />
                <TableHeading className="w-12" label="" />
              </tr>
            </thead>

            <tbody className={loading ? 'opacity-40 pointer-events-none' : ''}>
              {orders?.orders.map((row: OrderType) => (
                <OrdersListingTableRow
                  key={row.confirmationNumber}
                  {...props}
                  {...row}
                />
              ))}
            </tbody>
          </motion.table>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!!orders?.orders.length && showPagination && (
          <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
            <ItemPagination
              itemsPerPage={itemsPerPage}
              totalItems={orders?.totalOrderCount || 0}
              onPageChange={(currentPage: number) => {
                setPage(currentPage);
                scrollToElement({
                  ref: ordersContainer.current,
                  offsetHeight: 20,
                  behavior: 'auto',
                });
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrdersListingTable;
